/**
 * Application Profile Wizard - 'Turbo-Tax' Style
 * Guided questionnaire for Business Application Teams
 * Based on the comprehensive Application Profile Template
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { extractErrorMessage } from "../utils/acceptSuggestions";
import {
  Server,
  Cloud,
  Database,
  Shield,
  Users,
  Clock,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Save,
  AlertTriangle,
  Building2,
  HardDrive,
  Globe,
  Key,
  Link2,
  Settings,
  FileText,
  Activity,
} from "lucide-react";

const STEPS = [
  { id: 1, title: "Application Info", icon: Server, description: "Basic application details" },
  { id: 2, title: "Usage & Capacity", icon: Users, description: "Peak usage and user counts" },
  { id: 3, title: "Business Owner", icon: Building2, description: "Owner contact information" },
  { id: 4, title: "Infrastructure", icon: HardDrive, description: "Deployment type selection" },
  { id: 5, title: "Technical Details", icon: Settings, description: "Infrastructure specifics" },
  { id: 6, title: "Database", icon: Database, description: "Database configuration" },
  { id: 7, title: "Security", icon: Shield, description: "Authentication & encryption" },
  { id: 8, title: "Dependencies", icon: Link2, description: "Service dependencies" },
  { id: 9, title: "DR Strategy", icon: Activity, description: "Disaster recovery plan" },
  { id: 10, title: "Vendor Info", icon: Globe, description: "Vendor contacts" },
  { id: 11, title: "Review", icon: FileText, description: "Final review" },
];

export default function ApplicationProfileWizard() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [task, setTask] = useState(null);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Access control logic:
  // - Business Application Owner (assigned user) can EDIT
  // - BCM Manager / IT-DRP Manager can only VIEW (read-only)
  const isAdminRole = user?.role === 'bcm_manager' || user?.role === 'it_drp_manager' || user?.role === 'admin';
  
  // Check if current user is the assigned owner of this task
  const isAssignedOwner = task?.assigned_to_email === user?.email || 
                          task?.assigned_to_id === user?.id ||
                          task?.created_by === user?.id;
  
  // Read-only if admin viewing someone else's task, or if task is already completed
  const isReadOnly = (isAdminRole && !isAssignedOwner) || task?.status === 'completed';
  
  // Form data state
  const [formData, setFormData] = useState({
    // Step 1: Application Info
    application_name: "",
    application_vendor: "",
    application_manager: "",
    application_admin_sme: "",
    application_purpose: "",
    locations_used: [],
    host_alias: "",
    functional_category: "",
    functional_category_other: "",
    
    // Step 2: Usage & Capacity
    peak_usage_days: [],
    peak_hours_start: "",
    peak_hours_end: "",
    peak_users_count: 0,
    off_peak_users_count: 0,
    
    // Step 3: Business Owner
    owner_name: "",
    owner_phone: "",
    owner_email: "",
    
    // Step 4: Infrastructure Type
    deployment_type: "",
    cloud_provider: "",
    cloud_regions: [],
    on_prem_datacenter: "",
    
    // Step 5a: On-Prem Details (if on_prem or hybrid)
    server_type: "",
    physical_server_details: "",
    vm_details: "",
    os_type: "",
    os_version: "",
    storage_type: [],
    storage_replication: "N/A",
    network_latency_requirement: "",
    network_throughput_requirement: "",
    citrix_required: "N/A",
    citrix_details: "",
    load_balancer: "N/A",
    load_balancer_details: "",
    firewall_dmz: "N/A",
    firewall_details: "",
    
    // Step 5b: Cloud Details (if cloud, saas, or hybrid)
    cloud_service_type: "",
    vendor_sla_rto: null,
    vendor_sla_rpo: null,
    iac_tool: "N/A",
    container_orchestration: "N/A",
    devops_pipeline: "N/A",
    auto_scaling_enabled: "N/A",
    multi_region_deployment: "N/A",
    backup_regions: [],
    
    // Step 6: Database
    database_type: [],
    database_version: "",
    database_replication: "N/A",
    immutable_backups: "N/A",
    backup_frequency: "",
    backup_retention_days: null,
    
    // Step 7: Security
    authentication_method: [],
    mfa_enabled: "N/A",
    identity_provider: "N/A",
    api_dependencies: [],
    ssl_certificates: "N/A",
    encryption_at_rest: "N/A",
    encryption_in_transit: "N/A",
    
    // Step 8: Dependencies
    upstream_dependencies: [],
    downstream_dependencies: [],
    third_party_integrations: [],
    file_share_mappings: [],
    
    // Step 9: DR Strategy
    dr_location: "",
    dr_deployment_strategy: "",
    strategy_rto_hours: 0,
    strategy_rpo_hours: 0,
    failover_process: "",
    restoration_process: "",
    dr_test_cases_prepared: "N/A",
    technical_recovery_plan_exists: "N/A",
    
    // Step 10: Vendor Info
    vendor_contact_name: "",
    vendor_contact_phone: "",
    vendor_contact_email: "",
    vendor_support_connectivity: "N/A",
    vendor_support_sla: "",
    
    // Step 11: Retirement
    retiring_within_24_months: "No",
    retirement_date: "",
    replacement_application: "",
    
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [taskId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [taskRes, optionsRes] = await Promise.all([
        api.get(`/application-profile/tasks/${taskId}`),
        api.get("/application-profile/template-options")
      ]);
      
      if (taskRes.data?.success) {
        setTask(taskRes.data.data);
        // Pre-populate from BIA
        setFormData(prev => ({
          ...prev,
          application_name: taskRes.data.data.application_name || ""
        }));
      }
      
      if (optionsRes.data?.success) {
        setOptions(optionsRes.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    if (isReadOnly) return; // Don't update in read-only mode
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayField = (field, value) => {
    if (isReadOnly) return; // Don't update in read-only mode
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const addDependency = (field, dependency) => {
    if (isReadOnly) return; // Don't update in read-only mode
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), dependency]
    }));
  };

  const removeDependency = (field, index) => {
    if (isReadOnly) return; // Don't update in read-only mode
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const nextStep = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
      // Update task progress
      api.put(`/application-profile/tasks/${taskId}/status`, null, {
        params: {
          status: "in_progress",
          completion_percentage: Math.round((currentStep / STEPS.length) * 100)
        }
      });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const submitProfile = async () => {
    setSaving(true);
    try {
      const profileData = {
        bia_id: task.bia_id,
        application_info: {
          application_name: formData.application_name,
          application_vendor: formData.application_vendor || null,
          application_manager: formData.application_manager,
          application_admin_sme: formData.application_admin_sme,
          application_purpose: formData.application_purpose,
          locations_used: formData.locations_used,
          host_alias: formData.host_alias || null,
          functional_category: formData.functional_category,
          functional_category_other: formData.functional_category_other || null
        },
        usage_capacity: {
          peak_usage_days: formData.peak_usage_days,
          peak_hours_start: formData.peak_hours_start || null,
          peak_hours_end: formData.peak_hours_end || null,
          peak_users_count: parseInt(formData.peak_users_count) || 0,
          off_peak_users_count: parseInt(formData.off_peak_users_count) || 0
        },
        business_owner: {
          owner_name: formData.owner_name,
          owner_phone: formData.owner_phone,
          owner_email: formData.owner_email
        },
        infrastructure_type: {
          deployment_type: formData.deployment_type,
          cloud_provider: formData.cloud_provider || null,
          cloud_regions: formData.cloud_regions,
          on_prem_datacenter: formData.on_prem_datacenter || null
        },
        on_prem_infrastructure: ["on_prem", "hybrid"].includes(formData.deployment_type) ? {
          server_type: formData.server_type,
          physical_server_details: formData.physical_server_details || null,
          vm_details: formData.vm_details || null,
          os_type: formData.os_type,
          os_version: formData.os_version,
          storage_type: formData.storage_type,
          storage_replication: formData.storage_replication,
          network_latency_requirement: formData.network_latency_requirement || null,
          network_throughput_requirement: formData.network_throughput_requirement || null,
          citrix_required: formData.citrix_required,
          citrix_details: formData.citrix_details || null,
          load_balancer: formData.load_balancer,
          load_balancer_details: formData.load_balancer_details || null,
          firewall_dmz: formData.firewall_dmz,
          firewall_details: formData.firewall_details || null
        } : null,
        cloud_infrastructure: ["cloud", "saas", "hybrid"].includes(formData.deployment_type) ? {
          cloud_service_type: formData.cloud_service_type,
          vendor_sla_rto: formData.vendor_sla_rto ? parseInt(formData.vendor_sla_rto) : null,
          vendor_sla_rpo: formData.vendor_sla_rpo ? parseInt(formData.vendor_sla_rpo) : null,
          iac_tool: formData.iac_tool,
          container_orchestration: formData.container_orchestration,
          devops_pipeline: formData.devops_pipeline,
          auto_scaling_enabled: formData.auto_scaling_enabled,
          multi_region_deployment: formData.multi_region_deployment,
          backup_regions: formData.backup_regions
        } : null,
        database_config: {
          database_type: formData.database_type,
          database_version: formData.database_version || null,
          database_replication: formData.database_replication,
          immutable_backups: formData.immutable_backups,
          backup_frequency: formData.backup_frequency || null,
          backup_retention_days: formData.backup_retention_days ? parseInt(formData.backup_retention_days) : null
        },
        security_config: {
          authentication_method: formData.authentication_method,
          mfa_enabled: formData.mfa_enabled,
          identity_provider: formData.identity_provider,
          api_dependencies: formData.api_dependencies,
          ssl_certificates: formData.ssl_certificates,
          encryption_at_rest: formData.encryption_at_rest,
          encryption_in_transit: formData.encryption_in_transit
        },
        dependencies_config: {
          upstream_dependencies: formData.upstream_dependencies,
          downstream_dependencies: formData.downstream_dependencies,
          third_party_integrations: formData.third_party_integrations,
          file_share_mappings: formData.file_share_mappings
        },
        dr_strategy: {
          dr_location: formData.dr_location,
          dr_deployment_strategy: formData.dr_deployment_strategy,
          strategy_rto_hours: parseInt(formData.strategy_rto_hours) || 0,
          strategy_rpo_hours: parseInt(formData.strategy_rpo_hours) || 0,
          failover_process: formData.failover_process,
          restoration_process: formData.restoration_process,
          dr_test_cases_prepared: formData.dr_test_cases_prepared,
          technical_recovery_plan_exists: formData.technical_recovery_plan_exists
        },
        vendor_info: {
          vendor_contact_name: formData.vendor_contact_name || null,
          vendor_contact_phone: formData.vendor_contact_phone || null,
          vendor_contact_email: formData.vendor_contact_email || null,
          vendor_support_connectivity: formData.vendor_support_connectivity,
          vendor_support_sla: formData.vendor_support_sla || null
        },
        retirement_info: {
          retiring_within_24_months: formData.retiring_within_24_months,
          retirement_date: formData.retirement_date || null,
          replacement_application: formData.replacement_application || null
        },
        notes: formData.notes || null
      };

      const response = await api.post("/application-profile", profileData);
      
      if (response.data?.success) {
        navigate("/it-drp-admin");
      }
    } catch (error) {
      console.error("Failed to submit profile:", error);
      toast.error(extractErrorMessage(error, "Failed to submit profile"));
    } finally {
      setSaving(false);
    }
  };

  const progress = Math.round((currentStep / STEPS.length) * 100);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-3 text-sky-700">
          <div className="w-6 h-6 border-2 border-sky-700 border-t-transparent rounded-full animate-spin" />
          <span>Loading Application Profile Wizard...</span>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Application Name *</Label>
                <Input
                  value={formData.application_name}
                  onChange={(e) => updateField("application_name", e.target.value)}
                  placeholder="Enter application name"
                />
              </div>
              <div>
                <Label>Application Vendor</Label>
                <Input
                  value={formData.application_vendor}
                  onChange={(e) => updateField("application_vendor", e.target.value)}
                  placeholder="e.g., Microsoft, Oracle, Custom"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Application Manager *</Label>
                <Input
                  value={formData.application_manager}
                  onChange={(e) => updateField("application_manager", e.target.value)}
                  placeholder="Name of application manager"
                />
              </div>
              <div>
                <Label>Application Admin/SME *</Label>
                <Input
                  value={formData.application_admin_sme}
                  onChange={(e) => updateField("application_admin_sme", e.target.value)}
                  placeholder="Technical SME name"
                />
              </div>
            </div>
            <div>
              <Label>Application Purpose *</Label>
              <Textarea
                value={formData.application_purpose}
                onChange={(e) => updateField("application_purpose", e.target.value)}
                placeholder="Describe the primary purpose of this application..."
                rows={3}
              />
            </div>
            <div>
              <Label>Functional Category *</Label>
              <Select value={formData.functional_category} onValueChange={(v) => updateField("functional_category", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {options?.functional_categories?.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.functional_category === "Other" && (
                <Input
                  className="mt-2"
                  value={formData.functional_category_other}
                  onChange={(e) => updateField("functional_category_other", e.target.value)}
                  placeholder="Specify other category"
                />
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div>
              <Label>Peak Usage Days *</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {options?.days_of_week?.map((day) => (
                  <Button
                    key={day}
                    type="button"
                    variant={formData.peak_usage_days.includes(day) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleArrayField("peak_usage_days", day)}
                  >
                    {day.slice(0, 3)}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Peak Hours Start</Label>
                <Input
                  type="time"
                  value={formData.peak_hours_start}
                  onChange={(e) => updateField("peak_hours_start", e.target.value)}
                />
              </div>
              <div>
                <Label>Peak Hours End</Label>
                <Input
                  type="time"
                  value={formData.peak_hours_end}
                  onChange={(e) => updateField("peak_hours_end", e.target.value)}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Peak Users Count *</Label>
                <Input
                  type="number"
                  value={formData.peak_users_count}
                  onChange={(e) => updateField("peak_users_count", e.target.value)}
                  placeholder="Number of concurrent users"
                />
              </div>
              <div>
                <Label>Off-Peak Users Count</Label>
                <Input
                  type="number"
                  value={formData.off_peak_users_count}
                  onChange={(e) => updateField("off_peak_users_count", e.target.value)}
                  placeholder="Number of users during off-peak"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <Label>Business Owner Name *</Label>
              <Input
                value={formData.owner_name}
                onChange={(e) => updateField("owner_name", e.target.value)}
                placeholder="Full name of business owner"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={formData.owner_phone}
                  onChange={(e) => updateField("owner_phone", e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={formData.owner_email}
                  onChange={(e) => updateField("owner_email", e.target.value)}
                  placeholder="owner@company.com"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div>
              <Label>Deployment Type *</Label>
              <p className="text-sm text-slate-500 mb-3">Select how this application is deployed</p>
              <div className="grid md:grid-cols-2 gap-3">
                {options?.deployment_types?.map((type) => (
                  <div
                    key={type.value}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.deployment_type === type.value
                        ? "border-sky-700 bg-sky-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    onClick={() => updateField("deployment_type", type.value)}
                  >
                    <div className="flex items-center gap-3">
                      {type.value === "on_prem" && <HardDrive className="w-6 h-6 text-slate-600" />}
                      {type.value === "cloud" && <Cloud className="w-6 h-6 text-blue-600" />}
                      {type.value === "saas" && <Globe className="w-6 h-6 text-emerald-600" />}
                      {type.value === "hybrid" && <Activity className="w-6 h-6 text-purple-600" />}
                      {type.value === "N/A" && <AlertTriangle className="w-6 h-6 text-amber-600" />}
                      <div>
                        <p className="font-medium">{type.label}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {["cloud", "saas", "hybrid"].includes(formData.deployment_type) && (
              <div>
                <Label>Cloud Provider</Label>
                <Select value={formData.cloud_provider} onValueChange={(v) => updateField("cloud_provider", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select cloud provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.cloud_providers?.map((provider) => (
                      <SelectItem key={provider} value={provider}>{provider}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {["on_prem", "hybrid"].includes(formData.deployment_type) && (
              <div>
                <Label>On-Premises Data Center</Label>
                <Input
                  value={formData.on_prem_datacenter}
                  onChange={(e) => updateField("on_prem_datacenter", e.target.value)}
                  placeholder="e.g., Primary DC - Building A"
                />
              </div>
            )}
          </div>
        );

      case 5:
        // Dynamic based on deployment type
        const isOnPrem = ["on_prem", "hybrid"].includes(formData.deployment_type);
        const isCloud = ["cloud", "saas", "hybrid"].includes(formData.deployment_type);
        
        return (
          <div className="space-y-6">
            {isOnPrem && (
              <div className="p-4 border rounded-lg bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <HardDrive className="w-5 h-5" /> On-Premises Configuration
                </h4>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Server Type *</Label>
                      <Select value={formData.server_type} onValueChange={(v) => updateField("server_type", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select server type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="physical">Physical Server</SelectItem>
                          <SelectItem value="virtual">Virtual Machine</SelectItem>
                          <SelectItem value="both">Both Physical & Virtual</SelectItem>
                          <SelectItem value="N/A">N/A</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Operating System *</Label>
                      <Input
                        value={formData.os_type}
                        onChange={(e) => updateField("os_type", e.target.value)}
                        placeholder="e.g., Windows Server, RHEL, Ubuntu"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Storage Type *</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {options?.storage_types?.map((type) => (
                        <Button
                          key={type}
                          type="button"
                          variant={formData.storage_type.includes(type) ? "default" : "outline"}
                          size="sm"
                          onClick={() => toggleArrayField("storage_type", type)}
                        >
                          {type}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Citrix Required</Label>
                      <Select value={formData.citrix_required} onValueChange={(v) => updateField("citrix_required", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {options?.yes_no_na?.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Load Balancer</Label>
                      <Select value={formData.load_balancer} onValueChange={(v) => updateField("load_balancer", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {options?.yes_no_na?.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {isCloud && (
              <div className="p-4 border rounded-lg bg-blue-50">
                <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <Cloud className="w-5 h-5 text-blue-600" /> Cloud/SaaS Configuration
                </h4>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Cloud Service Type *</Label>
                      <Select value={formData.cloud_service_type} onValueChange={(v) => updateField("cloud_service_type", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select service type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="IaaS">IaaS (Infrastructure)</SelectItem>
                          <SelectItem value="PaaS">PaaS (Platform)</SelectItem>
                          <SelectItem value="SaaS">SaaS (Software)</SelectItem>
                          <SelectItem value="N/A">N/A</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>IaC Tool</Label>
                      <Select value={formData.iac_tool} onValueChange={(v) => updateField("iac_tool", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {options?.iac_tools?.map((tool) => (
                            <SelectItem key={tool} value={tool}>{tool}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>DevOps Pipeline</Label>
                      <Select value={formData.devops_pipeline} onValueChange={(v) => updateField("devops_pipeline", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Jenkins">Jenkins</SelectItem>
                          <SelectItem value="GitHub Actions">GitHub Actions</SelectItem>
                          <SelectItem value="Azure DevOps">Azure DevOps</SelectItem>
                          <SelectItem value="GitLab CI">GitLab CI</SelectItem>
                          <SelectItem value="None">None</SelectItem>
                          <SelectItem value="N/A">N/A</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Container Orchestration</Label>
                      <Select value={formData.container_orchestration} onValueChange={(v) => updateField("container_orchestration", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {options?.container_platforms?.map((platform) => (
                            <SelectItem key={platform} value={platform}>{platform}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Vendor SLA RTO (hours)</Label>
                      <Input
                        type="number"
                        value={formData.vendor_sla_rto || ""}
                        onChange={(e) => updateField("vendor_sla_rto", e.target.value)}
                        placeholder="e.g., 4"
                      />
                    </div>
                    <div>
                      <Label>Vendor SLA RPO (hours)</Label>
                      <Input
                        type="number"
                        value={formData.vendor_sla_rpo || ""}
                        onChange={(e) => updateField("vendor_sla_rpo", e.target.value)}
                        placeholder="e.g., 1"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Auto-Scaling Enabled</Label>
                      <Select value={formData.auto_scaling_enabled} onValueChange={(v) => updateField("auto_scaling_enabled", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {options?.yes_no_na?.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Multi-Region Deployment</Label>
                      <Select value={formData.multi_region_deployment} onValueChange={(v) => updateField("multi_region_deployment", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {options?.yes_no_na?.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <div>
              <Label>Database Type(s) *</Label>
              <p className="text-sm text-slate-500 mb-2">Select all that apply</p>
              <div className="flex flex-wrap gap-2">
                {options?.database_types?.map((db) => (
                  <Button
                    key={db}
                    type="button"
                    variant={formData.database_type.includes(db) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleArrayField("database_type", db)}
                  >
                    {db}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Database Version</Label>
                <Input
                  value={formData.database_version}
                  onChange={(e) => updateField("database_version", e.target.value)}
                  placeholder="e.g., 2019, 19c, 8.0"
                />
              </div>
              <div>
                <Label>Database Replication</Label>
                <Select value={formData.database_replication} onValueChange={(v) => updateField("database_replication", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.replication_types?.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Immutable Backups</Label>
                <Select value={formData.immutable_backups} onValueChange={(v) => updateField("immutable_backups", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.yes_no_na?.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Backup Retention (days)</Label>
                <Input
                  type="number"
                  value={formData.backup_retention_days || ""}
                  onChange={(e) => updateField("backup_retention_days", e.target.value)}
                  placeholder="e.g., 30"
                />
              </div>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-4">
            <div>
              <Label>Authentication Methods *</Label>
              <p className="text-sm text-slate-500 mb-2">Select all that apply</p>
              <div className="flex flex-wrap gap-2">
                {options?.authentication_methods?.map((auth) => (
                  <Button
                    key={auth}
                    type="button"
                    variant={formData.authentication_method.includes(auth) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleArrayField("authentication_method", auth)}
                  >
                    {auth}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>MFA Enabled</Label>
                <Select value={formData.mfa_enabled} onValueChange={(v) => updateField("mfa_enabled", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.yes_no_na?.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Encryption at Rest</Label>
                <Select value={formData.encryption_at_rest} onValueChange={(v) => updateField("encryption_at_rest", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.yes_no_na?.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Encryption in Transit</Label>
                <Select value={formData.encryption_in_transit} onValueChange={(v) => updateField("encryption_in_transit", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.yes_no_na?.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Document upstream and downstream dependencies for this application</p>
            <div>
              <Label>Upstream Dependencies (Services this app depends on)</Label>
              <Textarea
                value={formData.upstream_dependencies.join("\n")}
                onChange={(e) => updateField("upstream_dependencies", e.target.value.split("\n").filter(v => v.trim()))}
                placeholder="Enter each dependency on a new line"
                rows={3}
              />
            </div>
            <div>
              <Label>Downstream Dependencies (Services that depend on this app)</Label>
              <Textarea
                value={formData.downstream_dependencies.join("\n")}
                onChange={(e) => updateField("downstream_dependencies", e.target.value.split("\n").filter(v => v.trim()))}
                placeholder="Enter each dependency on a new line"
                rows={3}
              />
            </div>
            <div>
              <Label>Third-Party Integrations</Label>
              <Textarea
                value={formData.third_party_integrations.join("\n")}
                onChange={(e) => updateField("third_party_integrations", e.target.value.split("\n").filter(v => v.trim()))}
                placeholder="e.g., Salesforce, SAP, etc."
                rows={3}
              />
            </div>
          </div>
        );

      case 9:
        return (
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                <AlertTriangle className="w-5 h-5" />
                BIA Requirements
              </div>
              <p className="text-sm text-amber-600">
                Required RTO: <strong>{task?.required_rto || 24} hours</strong> | 
                Required RPO: <strong>{task?.required_rpo || 4} hours</strong>
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>DR Location *</Label>
                <Select value={formData.dr_location} onValueChange={(v) => updateField("dr_location", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select DR location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Azure">Azure</SelectItem>
                    <SelectItem value="AWS">AWS</SelectItem>
                    <SelectItem value="GCP">GCP</SelectItem>
                    <SelectItem value="On-Prem DC">On-Premises DC</SelectItem>
                    <SelectItem value="Hybrid">Hybrid</SelectItem>
                    <SelectItem value="N/A">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>DR Strategy *</Label>
                <Select value={formData.dr_deployment_strategy} onValueChange={(v) => updateField("dr_deployment_strategy", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select DR strategy" />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.dr_strategies?.map((strategy) => (
                      <SelectItem key={strategy.value} value={strategy.value}>
                        {strategy.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Strategy RTO (hours) *</Label>
                <Input
                  type="number"
                  value={formData.strategy_rto_hours}
                  onChange={(e) => updateField("strategy_rto_hours", e.target.value)}
                  placeholder="What RTO can be achieved?"
                />
                {parseInt(formData.strategy_rto_hours) > (task?.required_rto || 24) && (
                  <p className="text-xs text-red-600 mt-1">
                    Warning: Strategy RTO exceeds required RTO of {task?.required_rto || 24}h
                  </p>
                )}
              </div>
              <div>
                <Label>Strategy RPO (hours) *</Label>
                <Input
                  type="number"
                  value={formData.strategy_rpo_hours}
                  onChange={(e) => updateField("strategy_rpo_hours", e.target.value)}
                  placeholder="What RPO can be achieved?"
                />
              </div>
            </div>
            <div>
              <Label>Failover Process</Label>
              <Textarea
                value={formData.failover_process}
                onChange={(e) => updateField("failover_process", e.target.value)}
                placeholder="Describe the failover process..."
                rows={3}
              />
            </div>
            <div>
              <Label>Restoration Process</Label>
              <Textarea
                value={formData.restoration_process}
                onChange={(e) => updateField("restoration_process", e.target.value)}
                placeholder="Describe the restoration/failback process..."
                rows={3}
              />
            </div>
          </div>
        );

      case 10:
        return (
          <div className="space-y-4">
            <div>
              <Label>Vendor Contact Name</Label>
              <Input
                value={formData.vendor_contact_name}
                onChange={(e) => updateField("vendor_contact_name", e.target.value)}
                placeholder="Primary vendor contact"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Vendor Phone</Label>
                <Input
                  value={formData.vendor_contact_phone}
                  onChange={(e) => updateField("vendor_contact_phone", e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div>
                <Label>Vendor Email</Label>
                <Input
                  type="email"
                  value={formData.vendor_contact_email}
                  onChange={(e) => updateField("vendor_contact_email", e.target.value)}
                  placeholder="support@vendor.com"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Support Connectivity</Label>
                <Select value={formData.vendor_support_connectivity} onValueChange={(v) => updateField("vendor_support_connectivity", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.vendor_support_types?.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Retiring within 24 months?</Label>
                <Select value={formData.retiring_within_24_months} onValueChange={(v) => updateField("retiring_within_24_months", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="N/A">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 11:
        return (
          <div className="space-y-6">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-700 font-medium mb-2">
                <CheckCircle2 className="w-5 h-5" />
                Review Your Application Profile
              </div>
              <p className="text-sm text-emerald-600">
                Please review all information before submitting.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-sm text-slate-500 mb-2">Application</h4>
                  <p className="font-semibold">{formData.application_name}</p>
                  <p className="text-sm text-slate-600">{formData.functional_category}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-sm text-slate-500 mb-2">Infrastructure</h4>
                  <p className="font-semibold capitalize">{formData.deployment_type?.replace('_', ' ')}</p>
                  {formData.cloud_provider && <p className="text-sm text-slate-600">{formData.cloud_provider}</p>}
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-sm text-slate-500 mb-2">Database</h4>
                  <p className="font-semibold">{formData.database_type.join(', ') || 'N/A'}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-sm text-slate-500 mb-2">DR Strategy</h4>
                  <p className="font-semibold capitalize">{formData.dr_deployment_strategy?.replace('_', ' ')}</p>
                  <p className="text-sm text-slate-600">Location: {formData.dr_location}</p>
                </div>
                <div className={`p-3 rounded-lg ${
                  parseInt(formData.strategy_rto_hours) > (task?.required_rto || 24) 
                    ? 'bg-red-50 border border-red-200' 
                    : 'bg-emerald-50'
                }`}>
                  <h4 className="font-medium text-sm text-slate-500 mb-2">RTO/RPO Alignment</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Required</p>
                      <p className="font-semibold">RTO: {task?.required_rto || 24}h / RPO: {task?.required_rpo || 4}h</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Strategy</p>
                      <p className="font-semibold">RTO: {formData.strategy_rto_hours}h / RPO: {formData.strategy_rpo_hours}h</p>
                    </div>
                  </div>
                  {parseInt(formData.strategy_rto_hours) > (task?.required_rto || 24) && (
                    <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Resiliency gap detected - Strategy RTO exceeds required RTO
                    </p>
                  )}
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-sm text-slate-500 mb-2">Security</h4>
                  <p className="font-semibold">{formData.authentication_method.join(', ') || 'N/A'}</p>
                  <p className="text-sm text-slate-600">MFA: {formData.mfa_enabled}</p>
                </div>
              </div>
            </div>

            <div>
              <Label>Additional Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Any additional information..."
                rows={3}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="application-profile-wizard">
      {/* Read-Only Banner for Admin Users */}
      {isReadOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">View-Only Mode</p>
            <p className="text-sm text-amber-700">
              As a {user?.role === 'it_drp_manager' ? 'IT-DRP Manager' : 'BCM Manager'}, you can view this profile but cannot edit it. 
              Only the Business Application Owner can complete this profile.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            Application Profile
          </Badge>
          <Badge variant="outline">{task?.business_unit}</Badge>
          {isReadOnly && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-800">
              Read-Only
            </Badge>
          )}
        </div>
        <h1 className="text-2xl font-bold text-slate-900">{task?.application_name || "Application Profile"}</h1>
        <p className="text-slate-500">
          {isReadOnly 
            ? "Viewing application profile (read-only)"
            : "Complete the application profile to enable IT-DRP planning"
          }
        </p>
      </div>

      {/* Progress */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">Step {currentStep} of {STEPS.length}</span>
            <span className="text-sm font-medium text-sky-700">{progress}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between mt-4 overflow-x-auto pb-2">
            {STEPS.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`flex flex-col items-center min-w-[60px] cursor-pointer ${
                    currentStep === step.id ? 'text-sky-700' :
                    currentStep > step.id ? 'text-emerald-600' : 'text-slate-400'
                  }`}
                  onClick={() => setCurrentStep(step.id)}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                    currentStep === step.id ? 'bg-sky-100' :
                    currentStep > step.id ? 'bg-emerald-100' : 'bg-slate-100'
                  }`}>
                    {currentStep > step.id ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <span className="text-xs text-center whitespace-nowrap">{step.title}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Step Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {(() => {
              const StepIcon = STEPS[currentStep - 1].icon;
              return <StepIcon className="w-5 h-5 text-sky-700" />;
            })()}
            {STEPS[currentStep - 1].title}
          </CardTitle>
          <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
        </CardHeader>
        <CardContent>
          {renderStepContent()}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          <ChevronLeft className="w-4 h-4 mr-2" /> Previous
        </Button>
        
        {currentStep === STEPS.length ? (
          isReadOnly ? (
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
            >
              <ChevronLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
          ) : (
            <Button
              onClick={submitProfile}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-profile-btn"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Submit Profile
                </>
              )}
            </Button>
          )
        ) : (
          <Button onClick={nextStep}>
            Next <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
